import React, { useState, useEffect } from 'react';
import { agenciesAPI } from '../../services/api';
import Button from '../common/Button';
import { handleApiError } from '../../utils/helpers';

const AgencyFormModal = ({ agency, plans, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    admin_email: '',
    admin_name: '',
    admin_password: '',
    plan_id: '',
    subscription_status: 'free',
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (agency) {
      setFormData({
        name: agency.name || '',
        admin_email: agency.admin_email || '',
        admin_name: agency.admin_name || '',
        admin_password: '', // Don't show password for existing agency
        plan_id: agency.plan_id || '',
        subscription_status: agency.subscription_status || 'free',
        is_active: agency.is_active !== false,
      });
    }
  }, [agency]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (agency) {
        // Update existing agency
        await agenciesAPI.update(agency.id, formData);
      } else {
        // Create new agency
        if (!formData.name || !formData.admin_email || !formData.plan_id) {
          throw new Error('Name, Admin Email, and Plan are required');
        }
        if (!formData.admin_password || formData.admin_password.length < 6) {
          throw new Error('Admin Password is required (minimum 6 characters)');
        }
        await agenciesAPI.create(formData);
      }
      onSuccess();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {agency ? 'Edit Agency' : 'Add New Agency'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Agency Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Agency Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Creative Agency Inc."
                className="input w-full"
              />
            </div>

            {/* Admin Email */}
            <div>
              <label htmlFor="admin_email" className="block text-sm font-medium text-gray-700 mb-2">
                Admin Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="admin_email"
                name="admin_email"
                required={!agency}
                value={formData.admin_email}
                onChange={handleChange}
                disabled={!!agency}
                placeholder="admin@agency.com"
                className="input w-full"
              />
              {agency && (
                <p className="text-xs text-gray-500 mt-1">
                  Admin email cannot be changed after creation
                </p>
              )}
            </div>

            {/* Admin Name (optional when creating) */}
            {!agency && (
              <div>
                <label htmlFor="admin_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Name
                </label>
                <input
                  type="text"
                  id="admin_name"
                  name="admin_name"
                  value={formData.admin_name}
                  onChange={handleChange}
                  placeholder="e.g., John Doe"
                  className="input w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional - email prefix will be used if not provided
                </p>
              </div>
            )}

            {/* Admin Password (only when creating new agency) */}
            {!agency && (
              <div>
                <label htmlFor="admin_password" className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  id="admin_password"
                  name="admin_password"
                  required
                  value={formData.admin_password}
                  onChange={handleChange}
                  placeholder="Minimum 6 characters"
                  minLength="6"
                  className="input w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  The admin will use this email and password to login
                </p>
              </div>
            )}

            {/* Plan Selection */}
            <div>
              <label htmlFor="plan_id" className="block text-sm font-medium text-gray-700 mb-2">
                Subscription Plan <span className="text-red-500">*</span>
              </label>
              <select
                id="plan_id"
                name="plan_id"
                required
                value={formData.plan_id}
                onChange={handleChange}
                className="input w-full"
              >
                <option value="">Select a plan</option>
                {plans
                  .filter((plan) => plan.is_active !== false)
                  .map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - ${plan.price === 0 ? 'Free' : plan.price}
                    </option>
                  ))}
              </select>
            </div>

            {/* Subscription Status */}
            <div>
              <label
                htmlFor="subscription_status"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Subscription Status
              </label>
              <select
                id="subscription_status"
                name="subscription_status"
                value={formData.subscription_status}
                onChange={handleChange}
                className="input w-full"
              >
                <option value="free">Free</option>
                <option value="trial">Trial</option>
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            {/* Active Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                Agency is active
              </label>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Saving...' : agency ? 'Update Agency' : 'Create Agency'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AgencyFormModal;

